// components/FollowListModal/index.tsx
// フォロー一覧・詳細モーダルコンポーネント
// 「未確認フォロー」以外のフォロー一覧でも再利用可能な汎用コンポーネント

import React, { useState, useLayoutEffect, useRef } from 'react';
import dayjs from 'dayjs';
import {
  Modal, Button, List,
  Divider, Typography, Dropdown, Spin, message, Tag, Space,
} from 'antd';
import {
  SendOutlined,
  LinkOutlined,
  ShareAltOutlined,
  DownOutlined,
  EditOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import './FollowListModal.css';
import AttachmentSection, { AttachmentFile } from '../AttachmentSection';
import CommentSection from '../CommentSection';
import NotifRecipientModal, { AlertSettings } from '../NotifRecipientModal';
import FollowFormModal, { FollowFormValues, OnsiteCustomer } from '../FollowFormModal';
import CustomerSelectPanel from '../CustomerSelectPanel';
import { Customer } from '../../types/businessCard';
import { followAPI, buildNoticeDestinationList, buildInitialNotifRecipients, toFollowComment } from '../../services/follow-api';
import { NotifRecipientWithDetails, FollowComment } from '../../types/follow';
import { useAuth } from '../../contexts/AuthContext';
import { canEditFollow } from '../../utils/auth';
import { convertFollowResponseToFormValues, buildAlertSettingsFromFollow } from '../../utils/followDataConverter';
import { indexedDBStorage } from '../../services/storage/indexedDB';
import { previewAttachFile, downloadAttachFile } from '../../utils/fileOperations';
import { MODAL_Z } from '../../utils/modalZIndex';
import { useFollowFormFeatures } from '../../hooks/useFollowFormFeatures';
import { useRelatedFollowActions } from '../../hooks/useRelatedFollowActions';
import { useFollowComments } from '../../hooks/useFollowComments';

const { Text } = Typography;

// ----------------------------------------------------------------
// 型定義
// ----------------------------------------------------------------

/**
 * フォローアイテムの型（APIレスポンスのフィールド名に準拠）
 */
export interface FollowItem {
  followno: number;
  customerno?: number;
  followdate: string;        // フォロー日付
  followcontent: string;     // フォロー内容
  contactuserno?: number;    // 担当者userno
  createuserno?: number;     // 作成者userno（自フォロー判定に使用）
  contactusername: string;   // 担当者名
  customername: string;      // 顧客名
  companyname: string;       // 会社名
  attach?: boolean;          // 添付ファイルあり
  urgentflag: boolean;       // 至急フラグ
  temporaryflag?: boolean;   // 仮登録フラグ
  plantodoflag?: boolean;    // 予定ToDoフラグ
}

export interface ContactUser {
  userno: number;
  username: string;
  deptname: string;
}

// ----------------------------------------------------------------
// Props
// ----------------------------------------------------------------

export interface FollowListModalProps {
  /** 一覧モーダルの表示状態 */
  open: boolean;
  /** 一覧モーダルを閉じるコールバック */
  onClose: () => void;
  /** モーダルのタイトル（例: "未確認フォロー"） */
  title: string;
  /** 表示するフォローアイテム一覧 */
  items: FollowItem[];
  /** 通知先・担当者選択に使うユーザー一覧 */
  contactUsers: ContactUser[];
  /**
   * 機能フラグ（省略時はすべて有効）
   * - showMarkUnread: 「未読にする」ボタン表示
   * - showNotification: 「通知」ドロップダウン表示
   * - showLinkFollow: 「フォローの紐づけ」ボタン表示
   * - showRelatedFollow: 「関連フォロー表示」ボタン表示
   */
  features?: {
    showMarkUnread?: boolean;
    showNotification?: boolean;
    showLinkFollow?: boolean;
    showRelatedFollow?: boolean;
  };
  /**
   * 「未読にする」確定時に呼ばれるコールバック（省略可）
   * 一覧カウント更新などに使用する
   */
  onMarkUnread?: (item: FollowItem) => void;
  /**
   * フォロー更新・未読化後に一覧を再取得するコールバック（省略可）
   */
  onRefresh?: () => void;
}

// ----------------------------------------------------------------
// コンポーネント
// ----------------------------------------------------------------

const FollowListModal: React.FC<FollowListModalProps> = ({
  open,
  onClose,
  title,
  items,
  contactUsers,
  features = {},
  onMarkUnread,
  onRefresh,
}) => {
  const {
    showMarkUnread = true,
    showNotification = true,
    showLinkFollow = true,
    showRelatedFollow = true,
  } = features;

  // 通知先の初期選択（buildInitialNotifRecipients）でログインユーザーを除外するために使用
  const { user } = useAuth();

  // ベルのダイアログ（一覧）を開くたびに一覧の DOM を作り直し、必ず先頭から表示する。
  // フォロー詳細への遷移・編集更新では親 open が true のまま変化しないため作り直されず、位置は保持される。
  const [listOpenSeq, setListOpenSeq] = useState(0);
  useLayoutEffect(() => {
    if (open) setListOpenSeq((n) => n + 1);
  }, [open]);

  // フォロー詳細
  const [followDetailVisible, setFollowDetailVisible] = useState<boolean>(false);
  const [selectedFollowItem, setSelectedFollowItem] = useState<FollowItem | null>(null);

  // 詳細取得後の通知フラグ（selectFollow レスポンスから取得）
  const [noticeExistflag, setNoticeExistflag] = useState<boolean>(false);
  const [noticeUnreadflag, setNoticeUnreadflag] = useState<boolean>(false);

  // 詳細表示用の添付ファイル一覧（API取得）
  const [detailAttachments, setDetailAttachments] = useState<AttachmentFile[]>([]);

  // サブモーダル
  const [notifSendModalVisible, setNotifSendModalVisible] = useState<boolean>(false);
  const [notifHistoryVisible, setNotifHistoryVisible] = useState<boolean>(false);

  // 通知履歴
  const [notifHistoryItems, setNotifHistoryItems] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);

  // 通知送信モーダルの初期選択受信者
  const [notifInitialRecipients, setNotifInitialRecipients] = useState<NotifRecipientWithDetails[]>([]);
  // TODO: Teams通知実装時はコメントアウトを解除（Teamsチャネル一覧）
  // const [followNotifTeamsChannels, setFollowNotifTeamsChannels] = useState<{ url: string; name: string; defaultFlag?: boolean }[]>([]);

  // 詳細取得ローディング（カードタップ時の selectFollow 中）
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  // selectFollow で取得した詳細データ（編集フォームの初期値構築に再利用）
  const [selectedFollowDetail, setSelectedFollowDetail] = useState<any>(null);
  // selectedFollowDetail.followcomment を変換した安定参照（useFollowComments の initialComments に使用。
  // 呼び出しの都度 map() すると参照が毎回変わり、useFollowComments 側の同期 effect が意図せず毎レンダー発火してしまう）
  const [selectedFollowComments, setSelectedFollowComments] = useState<FollowComment[]>([]);

  // 関連フォロー（表示・紐づけ・切り離し）の共有ロジック。
  // anchor の groupNo は selectedFollowDetail（selectFollow の結果）から供給し、
  // 詳細の再取得で groupNo が変われば hasRelation も自動追従する。
  const relatedFollow = useRelatedFollowActions({
    anchor: selectedFollowItem
      ? {
          followNo: selectedFollowItem.followno,
          groupNo: selectedFollowDetail?.relationfollowgroupno ?? null,
          companyName: selectedFollowItem.companyname,
          customerName: selectedFollowItem.customername,
        }
      : null,
    contactUsers,
    relatedZIndex: MODAL_Z.L2,
    linkZIndex: MODAL_Z.L3,
  });

  // フォロー編集モーダルのコメント欄。followno は selectedFollowItem 切替で自動リセットされ、
  // selectedFollowComments（selectFollow 結果の followcomment を変換したもの）を初期値として使う。
  const followComments = useFollowComments({
    followno: selectedFollowItem?.followno ?? null,
    contactUsers,
    initialComments: selectedFollowComments,
    zIndex: MODAL_Z.L2,
  });

  // カード連打時に古い selectFollow 応答で新しい選択を上書きしないためのガード
  const latestClickRef = useRef<number | null>(null);

  // フォロー編集
  const [editFollowModalVisible, setEditFollowModalVisible] = useState<boolean>(false);
  const [editFollowInitialValues, setEditFollowInitialValues] = useState<Partial<FollowFormValues> | undefined>();

  // 同席者（FollowFormModal の制御モード用 state）
  const [onsiteCustomers, setOnsiteCustomers] = useState<OnsiteCustomer[]>([]);
  const [customerSelectVisible, setCustomerSelectVisible] = useState<boolean>(false);


  // 添付ファイル削除確認
  const [deleteAttachConfirmVisible, setDeleteAttachConfirmVisible] = useState<boolean>(false);
  const [deleteAttachTarget, setDeleteAttachTarget] = useState<AttachmentFile | null>(null);

  // ----------------------------------------------------------------
  // ハンドラー
  // ----------------------------------------------------------------

  /** フォローカード選択時：詳細取得（自動既読）＋添付ファイル取得 */
  const handleItemClick = async (item: FollowItem) => {
    // このクリックを最新として記録（連打時、古い応答は反映しない）
    latestClickRef.current = item.followno;
    setSelectedFollowItem(item);
    setSelectedFollowDetail(null);
    setSelectedFollowComments([]);
    setNoticeExistflag(false);
    setNoticeUnreadflag(false);
    setDetailAttachments([]);
    setDetailLoading(true);
    setFollowDetailVisible(true);
    // 関連フォロー state は anchor（selectedFollowItem/Detail）切替で自動リセットされる

    // 詳細取得（selectFollow が自動既読・noticeフラグ取得・編集フォーム用データ保持を兼ねる）
    try {
      const detail = await followAPI.selectFollow(item.followno);
      if (latestClickRef.current !== item.followno) return;
      // 群No は selectedFollowDetail から anchor 経由でフックへ渡る（手動初期化は不要）
      setSelectedFollowDetail(detail);
      setSelectedFollowComments((detail.followcomment || []).map(toFollowComment));
      setNoticeExistflag(detail.noticeExistflag ?? false);
      setNoticeUnreadflag(detail.noticeUnreadflag ?? false);
    } catch {
      // 詳細取得失敗はサイレントに処理（一覧表示は継続）
    } finally {
      if (latestClickRef.current === item.followno) setDetailLoading(false);
    }

    // 添付ファイル一覧取得（詳細取得と独立して実行）
    try {
      const res = await followAPI.getAttachmentList({ type: 1, targetno: item.followno });
      if (latestClickRef.current !== item.followno) return;
      setDetailAttachments(mapAttachFiles(res.attachfilelist));
    } catch {
      // 添付取得失敗はサイレントに処理
    }
  };

  /** 詳細モーダルを閉じる */
  const handleCloseDetail = () => {
    setFollowDetailVisible(false);
    setSelectedFollowDetail(null);
    setNoticeExistflag(false);
    setNoticeUnreadflag(false);
    setDetailAttachments([]);
    setNotifHistoryItems([]);
    setOnsiteCustomers([]);
    // selectedFollowItem は既存設計どおり保持するため、モーダルだけ閉じる
    relatedFollow.closeModals();
  };

  /** フォロー編集モーダルを閉じる */
  const handleCloseEditFollowModal = () => {
    setEditFollowModalVisible(false);
    setOnsiteCustomers([]);
    setCustomerSelectVisible(false);
  };

  /** フォロー編集モーダルを開く（カードタップ時に取得済みの詳細データを再利用） */
  const openEditFollowModal = () => {
    if (!selectedFollowDetail) return;
    const { formValues, attendeesList, attachList } = convertFollowResponseToFormValues(selectedFollowDetail);
    setOnsiteCustomers(attendeesList);
    setEditFollowInitialValues({ ...formValues, fileList: attachList });
    setEditFollowModalVisible(true);
  };

  /** フォロー更新送信 */
  const handleEditFollowSubmit = async (values: FollowFormValues) => {
    if (!selectedFollowItem || !selectedFollowDetail) return;
    try {
      const requestData: any = {
        followno: String(selectedFollowItem.followno),
        customerno: String(selectedFollowItem.customerno ?? ''),
        contactuserno: values.assigneeUserno != null ? String(values.assigneeUserno) : '',
        followdate: values.date + 'T00:00:00.000+0900',
        followcontent: values.content,
        temporaryflag: values.isProvisional,
        plantodoflag: values.isTodo,
        optionfield: Object.keys(values.optionFieldValues).length > 0
          ? JSON.stringify(values.optionFieldValues)
          : undefined,
      };

      // 予定/ToDoのアラート設定（事前アラート・繰り返しアラート）は本モーダルの編集画面では変更できないため、
      // 既存値をそのまま再送する（未送信だとサーバ側の全カラム更新で 0/false にリセットされてしまう）。
      const alertFromDb = buildAlertSettingsFromFollow(selectedFollowDetail);
      if (values.isTodo && alertFromDb) {
        requestData.prealert = alertFromDb.preAlert;
        requestData.realert = alertFromDb.reAlert;
        requestData.realertflag = alertFromDb.reAlertFlag;
      }

      if (values.isOnsite) {
        const [schedHour = '', schedMin = ''] = (values.followScheduledTime || '').split(':');
        const [startHour = '', startMin = ''] = (values.followStartTime || '').split(':');
        const [endHour = '', endMin = ''] = (values.followEndTime || '').split(':');
        requestData.outgovisitflag = true;
        requestData.outgovisitschedultimehour = schedHour;
        requestData.outgovisitschedultimemin = schedMin;
        requestData.outgovisitappoint = values.followAppointment || '';
        requestData.outgovisitpurpose = values.followVisitPurpose || '';
        requestData.outgovisitstarttimehour = startHour;
        requestData.outgovisitstarttimemin = startMin;
        requestData.outgovisitendtimehour = endHour;
        requestData.outgovisitendtimemin = endMin;
        requestData.attendeesno = JSON.stringify(values.onsiteCustomers.map(c => c.customerno).filter(Boolean));
        requestData.ourattendeesno = JSON.stringify(values.selectedInternalAttendees);
      }

      const result = await followAPI.registerFollow(requestData);
      if (result.success) {
        message.success('フォローを更新しました');
        setEditFollowModalVisible(false);
        // 詳細モーダルに保存済み内容を反映
        const assigneeUser = contactUsers.find(u => u.userno === values.assigneeUserno);
        const assigneeName = assigneeUser?.username
          ?? (values.assigneeUserno != null && values.assigneeUserno < 0 ? 'システム管理者' : undefined);
        setSelectedFollowItem(prev => prev ? {
          ...prev,
          followdate: values.date,
          followcontent: values.content,
          temporaryflag: values.isProvisional,
          contactuserno: values.assigneeUserno ?? undefined,
          contactusername: assigneeName ?? prev.contactusername,
        } : prev);
        if (onRefresh) onRefresh();
        // 詳細モーダルを最新状態に更新（連打で選択が別フォローに切り替わっていたら反映しない）
        const followNoForRefetch = selectedFollowItem.followno;
        setDetailLoading(true);
        try {
          const updated = await followAPI.selectFollow(followNoForRefetch);
          if (latestClickRef.current !== followNoForRefetch) return;
          setSelectedFollowDetail(updated);
        } catch {
        } finally {
          if (latestClickRef.current === followNoForRefetch) setDetailLoading(false);
        }
      } else {
        message.error(result.message || 'フォローの更新に失敗しました');
      }
    } catch {
      message.error('フォローの更新に失敗しました');
    }
  };

  /** 既読 → 未読 に切り替え（トグルの「未読にする」押下）
   *  詳細モーダルは閉じず、その場でトグル表示を未読へ更新する。 */
  const handleMarkUnread = async () => {
    if (!selectedFollowItem || noticeUnreadflag) return;
    try {
      await followAPI.markFollowUnread(selectedFollowItem.followno);
      setNoticeUnreadflag(true);
      // 親一覧（未確認フォロー件数バッジ・一覧）を再同期
      if (onMarkUnread) onMarkUnread(selectedFollowItem);
      if (onRefresh) onRefresh();
    } catch {
      message.error('未読化に失敗しました');
    }
  };

  /** 未読 → 既読 に切り替え（トグルの「既読にする」押下）
   *  既読化は UI 状態（noticeUnreadflag）の更新のみで完結する。 */
  const handleMarkRead = () => {
    if (!selectedFollowItem || !noticeUnreadflag) return;
    setNoticeUnreadflag(false);
  };

  /** 通知履歴モーダルを開く（APIから取得） */
  const handleOpenHistory = async () => {
    if (!selectedFollowItem) return;
    setNotifHistoryItems([]);
    setNotifHistoryVisible(true);
    setHistoryLoading(true);
    try {
      const items = await followAPI.getNoticeHistory(selectedFollowItem.followno);
      setNotifHistoryItems(items);
    } catch {
      message.error('通知履歴の取得に失敗しました');
    } finally {
      setHistoryLoading(false);
    }
  };

  /** 通知送信モーダルを開く */
  const handleOpenNotifSend = async (assigneeUserno: number | null, isTodo: boolean) => {
    if (!selectedFollowItem) return;
    setNotifInitialRecipients(
      await buildInitialNotifRecipients(selectedFollowItem.customerno, assigneeUserno, user?.userno, contactUsers, isTodo)
    );
    // TODO: Teams通知実装時はコメントアウトを解除（Teamsチャネル一覧の先取得）
    // if (selectedFollowItem.customerno) {
    //   const channels = await followAPI.getTeamsChannels(selectedFollowItem.customerno);
    //   setFollowNotifTeamsChannels(channels);
    // }
    setNotifSendModalVisible(true);
  };

  /** 通知送信確定 */
  const handleNotifSendSubmit = async (
    recipients: { userno: number; urgent: boolean }[],
    alertSettings?: AlertSettings,
    // TODO: メールアドレス通知実装時はコメントアウトを解除
    // emailAddresses: string[],
    // TODO: Teams通知実装時はコメントアウトを解除
    // teamsChannelUrls: string[],
  ) => {
    setNotifSendModalVisible(false);
    if (!selectedFollowItem) return;
    try {
      // TODO: Teams・メールアドレス通知実装時は buildNoticeDestinationList(recipients, emailAddresses, teamsChannelUrls) に戻してください
      const noticeDestinationList = buildNoticeDestinationList(recipients);
      const alertParams = alertSettings
        ? {
            plantodoflag: true,
            prealert: alertSettings.preAlert,
            realert: alertSettings.reAlert,
            realertflag: alertSettings.reAlertFlag,
          }
        : undefined;
      const warnMsg = await followAPI.sendNotice(selectedFollowItem.followno, noticeDestinationList, alertParams);
      if (warnMsg) {
        message.warning(warnMsg);
      } else {
        message.success('通知を送信しました');
      }
    } catch {
      message.error('通知の送信に失敗しました');
    }
  };

  // ----------------------------------------------------------------
  // 添付ファイル操作
  // ----------------------------------------------------------------

  /** attachfilelist → AttachmentFile[] マッピング共通ヘルパー */
  const mapAttachFiles = (list: any[]): AttachmentFile[] =>
    (list || []).map((f: any) => ({
      id: f.attachfileno,
      name: f.attachfilename || f.filename || '',
      size: parseFloat(f.attachfilesize || '0') * 1024,
    }));

  const {
    onAssistClick,
    onVoiceClick,
    onFileAdd,
    onContentSetterReady,
    openFileUploadModal,
    renderModals,
  } = useFollowFormFeatures({
    followno: selectedFollowItem?.followno ?? null,
    baseZIndex: MODAL_Z.L2,
    onAfterUpload: async () => {
      if (!selectedFollowItem) return;
      try {
        const res = await followAPI.getAttachmentList({ type: 1, targetno: selectedFollowItem.followno });
        setDetailAttachments(mapAttachFiles(res.attachfilelist));
      } catch {}
    },
  });

  /** ファイルプレビュー・ダウンロード共通処理 */
  const handleAttachFile = (file: AttachmentFile, action: 'preview' | 'download') => {
    const params = { attachfileno: Number(file.id), filename: file.name };
    if (action === 'preview') {
      previewAttachFile(params);
    } else {
      downloadAttachFile(params);
    }
  };

  const handleFilePreview = (file: AttachmentFile) => handleAttachFile(file, 'preview');
  const handleFileDownload = (file: AttachmentFile) => handleAttachFile(file, 'download');

  /** 添付ファイル削除確定 */
  const handleDeleteAttach = async () => {
    if (!deleteAttachTarget) return;
    try {
      const companyId = await indexedDBStorage.getItem('companyId') || '';
      await followAPI.deleteAttachment({
        attachfileno: Number(deleteAttachTarget.id),
        companyid: companyId,
      });
      setDetailAttachments(prev => prev.filter(f => f.id !== deleteAttachTarget.id));
      message.success('ファイルを削除しました');
    } catch {
      message.error('ファイルの削除に失敗しました');
    } finally {
      setDeleteAttachConfirmVisible(false);
      setDeleteAttachTarget(null);
    }
  };


  // ----------------------------------------------------------------
  // 共通スタイル
  // ----------------------------------------------------------------
  const rowStyle: React.CSSProperties = {
    display: 'flex', padding: '8px 0', borderBottom: '1px solid #f0f0f0',
    alignItems: 'flex-start', gap: 8, margin: '0 4px',
  };
  const labelStyle: React.CSSProperties = { width: 80, flexShrink: 0, fontSize: 13, color: '#8c8c8c' };
  const valueStyle: React.CSSProperties = { fontSize: 14, color: '#000', flex: 1, minWidth: 0, wordBreak: 'break-word', overflowWrap: 'break-word' };

  // ----------------------------------------------------------------
  // JSX
  // ----------------------------------------------------------------
  return (
    <>
      {/* フォロー一覧モーダル */}
      {(open && !followDetailVisible) && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 999 }} />
      )}
      <Modal
        open={open && !followDetailVisible}
        onCancel={onClose}
        footer={null}
        title={title}
        centered
        width={560}
        mask={false}
        styles={{ body: { padding: '8px 0' } }}
      >
        <div
          key={listOpenSeq}
          style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '8px 4px 16px', maxHeight: '70dvh', overflowY: 'auto', overflowX: 'hidden' }}
        >
          {items.map((item) => (
            <div
              key={item.followno}
              onClick={() => handleItemClick(item)}
              className={`follow-card${item.urgentflag ? ' follow-card--urgent' : ''}`}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 15, fontWeight: 'bold', color: '#000' }}>{item.companyname}</span>
                {item.temporaryflag && (
                  <Tag color="orange" style={{ fontSize: 11, padding: '0 4px', lineHeight: '18px', margin: 0 }}>仮</Tag>
                )}
                {item.plantodoflag && (
                  <Tag style={{ fontSize: 11, padding: '0 4px', lineHeight: '18px', margin: 0, backgroundColor: '#f6ffed', borderColor: '#52c41a', color: '#52c41a' }}>未</Tag>
                )}
              </div>
              <div style={{ fontSize: 14, color: '#000' }}>{item.customername}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                <Text style={{ fontSize: 10, color: '#8c8c8c' }}>日付：{item.followdate}</Text>
              </div>
              <div style={{ fontSize: 13, color: '#595959', marginTop: 2 }}>{item.followcontent}</div>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>担当：{item.contactuserno != null && item.contactuserno < 0 ? 'システム管理者' : item.contactusername}</div>
            </div>
          ))}
        </div>
      </Modal>

      {/* フォロー詳細モーダル */}
      {followDetailVisible && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: MODAL_Z.L1 - 1 }} />
      )}
      <Modal
        open={followDetailVisible}
        onCancel={handleCloseDetail}
        centered
        width={560}
        title="フォロー詳細"
        zIndex={MODAL_Z.L1}
        mask={false}
        styles={{ body: { padding: '16px 8px', maxHeight: '75dvh', overflowY: 'auto', overflowX: 'hidden' } }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={handleCloseDetail}>閉じる</Button>
            {/* 編集ボタン：全フォロー編集権限あり、または自分のフォロー（担当 or 作成）のみ表示 */}
            {canEditFollow(user, selectedFollowItem?.contactuserno, selectedFollowItem?.createuserno) && (
              <Button
                type="primary"
                icon={<EditOutlined />}
                loading={detailLoading}
                onClick={() => openEditFollowModal()}
              >
                編集
              </Button>
            )}
          </div>
        }
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin />
          </div>
        ) : selectedFollowDetail && (
          <>
            {/* 基本情報 */}
            <div style={rowStyle}>
              <div style={labelStyle}>フォローNo</div>
              <div style={valueStyle}>{selectedFollowDetail.followno}</div>
            </div>
            <div style={rowStyle}>
              <div style={labelStyle}>日付</div>
              <div style={valueStyle}>{selectedFollowDetail.followdate}</div>
            </div>
            <div style={rowStyle}>
              <div style={labelStyle}>担当者</div>
              <div style={valueStyle}>{selectedFollowDetail.contactuserno != null && selectedFollowDetail.contactuserno < 0 ? 'システム管理者' : selectedFollowDetail.contactusername}</div>
            </div>
            <div style={rowStyle}>
              <div style={labelStyle}>会社名</div>
              <div style={valueStyle}>{selectedFollowItem?.companyname}</div>
            </div>
            <div style={rowStyle}>
              <div style={labelStyle}>顧客名</div>
              <div style={valueStyle}>{selectedFollowItem?.customername}</div>
            </div>
            <div style={{ ...rowStyle, borderBottom: 'none' }}>
              <div style={labelStyle}>内容</div>
              <div style={{ ...valueStyle, whiteSpace: 'pre-wrap' }}>{selectedFollowDetail.followcontent}</div>
            </div>

              {/* コメント */}
              <Divider style={{ margin: '12px 0 4px' }} />
              <div style={{ margin: '0 4px 10px' }}>
                <CommentSection
                  comments={followComments.comments}
                  currentUserNo={user?.userno ?? null}
                  onAdd={followComments.openAdd}
                  onEdit={followComments.openEdit}
                  onDelete={followComments.requestDelete}
                  labelStyle={{ fontSize: 13, color: '#8c8c8c' }}
                />
              </div>

              {/* 添付ファイル */}
              <Divider style={{ margin: '12px 0 4px' }} />
              <AttachmentSection
                files={detailAttachments}
                onPreview={handleFilePreview}
                onDownload={handleFileDownload}
                onDelete={(file) => {
                  setDeleteAttachTarget(file);
                  setDeleteAttachConfirmVisible(true);
                }}
                onAdd={() => {
                  if (selectedFollowItem) openFileUploadModal(selectedFollowItem.followno);
                }}
              />

            {/* アクションセクション */}
            <Divider style={{ margin: '16px 0 0' }} />
            <div style={{ background: '#f5f5f5', borderRadius: 8, padding: '12px 16px', margin: '0 4px 4px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
              {/* 通知ドロップダウン：送信と履歴の2択 */}
              {showNotification && (
                <Dropdown
                  trigger={['click']}
                  menu={{
                    items: [
                      { key: 'send',    label: '送信', icon: <SendOutlined />,    onClick: () => { handleOpenNotifSend(selectedFollowItem?.contactuserno ?? null, !!selectedFollowDetail?.plantodoflag); } },
                      { key: 'history', label: '履歴', icon: <HistoryOutlined />, onClick: () => { handleOpenHistory(); } },
                    ],
                  }}
                >
                  <Button size="small" icon={<SendOutlined />}>
                    通知 <DownOutlined style={{ fontSize: 10 }} />
                  </Button>
                </Dropdown>
              )}

              {/* 既読/未読トグル：通知が存在する場合に表示。
                  antd Button で組むことで通知ボタン（size="small"）と高さを自動的に揃える。
                  現在地（既読 or 未読）を塗りつぶしの強調色で示し、反対側がアクションになる。 */}
              {showMarkUnread && noticeExistflag && (
                <Space.Compact size="small" aria-label="既読・未読の切り替え">
                  {/* 既読セグメント（左）：既読時は現在地として強調（非操作）、未読時は「既読にする」操作 */}
                  <Button
                    size="small"
                    onClick={noticeUnreadflag ? handleMarkRead : undefined}
                    style={
                      !noticeUnreadflag
                        ? { backgroundColor: '#8c8c8c', borderColor: '#8c8c8c', color: '#fff', fontWeight: 600, pointerEvents: 'none' }
                        : undefined
                    }
                  >
                    {noticeUnreadflag ? '既読にする' : '既読'}
                  </Button>
                  {/* 未読セグメント（右）：未読時は現在地として強調（非操作）、既読時は「未読にする」操作 */}
                  <Button
                    size="small"
                    onClick={!noticeUnreadflag ? handleMarkUnread : undefined}
                    style={
                      noticeUnreadflag
                        ? { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff', fontWeight: 600, pointerEvents: 'none' }
                        : undefined
                    }
                  >
                    {noticeUnreadflag ? '未読' : '未読にする'}
                  </Button>
                </Space.Compact>
              )}

              {/* 関連フォロー表示 */}
              {showRelatedFollow && relatedFollow.hasRelation && (
                <Button
                  size="small"
                  icon={<ShareAltOutlined />}
                  onClick={relatedFollow.openRelatedFollow}
                >
                  関連フォロー表示
                </Button>
              )}

              {/* フォローの紐づけ */}
              {showLinkFollow && (
                <Button
                  size="small"
                  icon={<LinkOutlined />}
                  onClick={relatedFollow.openLinkFollow}
                >
                  フォローの紐づけ
                </Button>
              )}
            </div>
          </>
        )}
      </Modal>

      {/* フォロー更新モーダル */}
      <FollowFormModal
        open={editFollowModalVisible}
        mode="edit"
        onCancel={handleCloseEditFollowModal}
        onSubmit={handleEditFollowSubmit}
        contactUsers={contactUsers}
        initialValues={editFollowInitialValues}
        defaultAssigneeUserno={user?.userno ?? null}
        zIndex={MODAL_Z.L2}
        onNotifSendClick={handleOpenNotifSend}
        onNotifHistoryClick={handleOpenHistory}
        onAssistClick={onAssistClick}
        onVoiceClick={onVoiceClick}
        onFileAdd={onFileAdd}
        onContentSetterReady={onContentSetterReady}
        controlledOnsiteCustomers={onsiteCustomers}
        onOnsiteCustomerRemove={customerno =>
          setOnsiteCustomers(prev => prev.filter(c => c.customerno !== customerno))
        }
        onCustomerSelectOpen={() => setCustomerSelectVisible(true)}
        onOnsiteCustomersClear={() => setOnsiteCustomers([])}
        onShowRelatedFollow={showRelatedFollow && relatedFollow.hasRelation ? () => {
          handleCloseEditFollowModal();
          relatedFollow.openRelatedFollow();
        } : undefined}
        onShowLinkFollow={showLinkFollow ? () => {
          handleCloseEditFollowModal();
          relatedFollow.openLinkFollow();
        } : undefined}
        comments={followComments.comments}
        onAddComment={followComments.openAdd}
        onEditComment={followComments.openEdit}
        onDeleteComment={followComments.requestDelete}
        currentUserNo={user?.userno ?? null}
      />

      {/* 顧客選択パネル（同席者追加） */}
      <CustomerSelectPanel
        visible={customerSelectVisible}
        onClose={() => setCustomerSelectVisible(false)}
        defaultCompany={selectedFollowItem?.companyname}
        excludeCustomerNo={selectedFollowItem?.customerno}
        selectedCustomersFromParent={onsiteCustomers as Customer[]}
        onSelectionChange={customers =>
          setOnsiteCustomers(customers.map(c => ({
            customerno: c.customerno,
            customername: c.customername,
            companyname: c.companyname || '',
            deptname: c.deptname || '',
          })))
        }
        zIndex={MODAL_Z.L3}
      />

      {renderModals()}

      {/* 添付ファイル削除確認モーダル */}
      {deleteAttachConfirmVisible && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: MODAL_Z.L3 - 1 }} />
      )}
      <Modal
        title="ファイルの削除"
        open={deleteAttachConfirmVisible}
        okText="削除"
        okType="danger"
        cancelText="キャンセル"
        centered
        mask={false}
        zIndex={MODAL_Z.L3}
        onCancel={() => { setDeleteAttachConfirmVisible(false); setDeleteAttachTarget(null); }}
        onOk={handleDeleteAttach}
      >
        <p>「{deleteAttachTarget?.name}」を削除しますか？</p>
      </Modal>

      {/* 通知履歴モーダル */}
      {notifHistoryVisible && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: MODAL_Z.L2 - 1 }} />
      )}
      <Modal
        title="フォロー通知履歴"
        open={notifHistoryVisible}
        onCancel={() => setNotifHistoryVisible(false)}
        centered
        width={480}
        mask={false}
        zIndex={MODAL_Z.L2}
        footer={<Button onClick={() => setNotifHistoryVisible(false)}>閉じる</Button>}
      >
        {historyLoading ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Spin />
          </div>
        ) : (
          <div style={{ border: '1px solid #f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
            {/* ヘッダー行 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '6px 10px', background: '#fafafa', borderBottom: '1px solid #f0f0f0', fontSize: 13, fontWeight: 'bold', color: 'rgba(0,0,0,0.65)' }}>
              <div>通知日時</div>
              <div>部署</div>
              <div>名前</div>
            </div>
            {/* データ行 */}
            {notifHistoryItems.length === 0 ? (
              <div style={{ padding: '16px 10px', textAlign: 'center', color: '#8c8c8c', fontSize: 13 }}>
                通知履歴はありません
              </div>
            ) : (
              notifHistoryItems.map((h, i) => (
                <div
                  key={h.noticeunitno ?? i}
                  style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '7px 10px', borderBottom: '1px solid #f5f5f5', alignItems: 'center', backgroundColor: h.urgentflag ? '#fff1f0' : undefined }}
                >
                  <div style={{ fontSize: 12, color: '#595959' }}>{(h.noticedatetime || h.noticedate) ? dayjs(h.noticedatetime || h.noticedate).format('YYYY-MM-DD HH:mm') : ''}</div>
                  <div style={{ fontSize: 13 }}>{h.deptname || ''}</div>
                  <div style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
                    {h.urgentflag && (
                      <span style={{ fontSize: 10, color: '#fa541c', fontWeight: 'bold', border: '1px solid #fa541c', borderRadius: 2, padding: '0 3px', lineHeight: '16px' }}>至急</span>
                    )}
                    {h.username || h.name || ''}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </Modal>

      {/* 通知先設定モーダル（mask={false} はコンポーネントのデフォルト） */}
      <NotifRecipientModal
        open={notifSendModalVisible}
        onCancel={() => setNotifSendModalVisible(false)}
        onSubmit={(recipients, _teams, _emails, alertSettings) => handleNotifSendSubmit(recipients, alertSettings)}
        contactUsers={contactUsers}
        initialRecipients={notifInitialRecipients}
        // TODO: Teams通知実装時はコメントアウトを解除
        // teamsChannels={followNotifTeamsChannels}
        showConfirm
        zIndex={MODAL_Z.L3}
        showAlertSettings={!!selectedFollowDetail?.plantodoflag}
        initialAlertSettings={buildAlertSettingsFromFollow(selectedFollowDetail ?? {})}
      />

      {/* 関連フォロー（紐づけ選択・関連フォロー表示）モーダル */}
      {relatedFollow.renderModals()}

      {/* フォローコメント（追加・編集・削除・通知先選択）モーダル */}
      {followComments.renderModals()}
    </>
  );
};

export default FollowListModal;
